import MuiIconButton from '@mui/material/IconButton';
import withStyles from '@mui/styles/withStyles';
import React, {useMemo} from 'react';

import type {IconButtonProps} from '@mui/material/IconButton/IconButton';

type Variant = 'primary' | 'secondary' | 'tertiary';

export interface Props extends Omit<IconButtonProps, 'color'> {
    variant?: Variant | undefined;
    samaritan?: boolean;
}

export const IconButton = (props: Props) => {
    const {children, disabled = false, variant = 'primary', samaritan = false, ...rest} = props;

    const IconButtonComponent = useMemo(() => {
        if (samaritan) {
            switch (variant) {
                case 'primary':
                    return PrimarySamaritanIconButton;
                case 'secondary':
                    return SecondarySamaritanIconButton;
                case 'tertiary':
                    return TertiarySamaritanIconButton;
                default:
                    return PrimarySamaritanIconButton;
            }
        }

        switch (variant) {
            case 'primary':
                return PrimaryIconButton;
            case 'secondary':
                return SecondaryIconButton;
            case 'tertiary':
                return TertiaryIconButton;
            default:
                return PrimaryIconButton;
        }
    }, [variant, samaritan]);

    return (
        <IconButtonComponent disabled={disabled} disableRipple disableFocusRipple disableTouchRipple {...rest}>
            {children}
        </IconButtonComponent>
    );
};

const PrimaryIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary['500'],
        padding: '0.6rem',
        '&:hover': {
            backgroundColor: theme.palette.primary['700'],
        },
        '&:active': {
            backgroundColor: theme.palette.primary['800'],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey['100'],
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);

const SecondaryIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.primary['500'],
        backgroundColor: theme.palette.grey['20'],
        borderColor: theme.palette.primary['500'],
        borderWidth: '0.2rem',
        borderStyle: 'solid',
        padding: '0.6rem',
        '&:hover': {
            color: theme.palette.primary['700'],
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.primary['700'],
        },
        '&:active': {
            color: theme.palette.primary['800'],
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.primary['800'],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.grey['100'],
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);

const TertiaryIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.grey['600'],
        padding: '0.6rem',
        '&:hover': {
            backgroundColor: theme.palette.primary['20'],
            color: theme.palette.primary['700'],
        },
        '&:active': {
            backgroundColor: theme.palette.primary['100'],
            color: theme.palette.primary['800'],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);

const PrimarySamaritanIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.common.white,
        background: `linear-gradient(90deg, ${theme.palette.samaritan['500']} 0%, ${theme.palette.indigo['500']} 100%)`,
        padding: '0.6rem',
        '&:hover': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['700']} 0%, ${theme.palette.indigo['700']} 100%)`,
        },
        '&:active': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['800']} 0%, ${theme.palette.indigo['800']} 100%)`,
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            background: theme.palette.grey['100'],
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);

const SecondarySamaritanIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.lilac['500'],
        backgroundColor: theme.palette.grey['20'],
        borderColor: theme.palette.lilac['500'],
        borderWidth: '0.2rem',
        borderStyle: 'solid',
        padding: '0.6rem',
        '&:hover': {
            color: theme.palette.lilac['700'],
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.lilac['700'],
        },
        '&:active': {
            color: theme.palette.lilac['800'],
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.lilac['800'],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.grey['100'],
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);

const TertiarySamaritanIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.samaritan['600'],
        padding: '0.6rem',
        '&:hover': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['20']} 0%, ${theme.palette.indigo['20']} 100%)`,
            color: theme.palette.samaritan['700'],
        },
        '&:active': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['50']} 0%, ${theme.palette.indigo['50']} 100%)`,

            color: theme.palette.samaritan['800'],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);
